import { useEffect, useState } from "react";
import {
  DialogContent,
  Switch,
  IconButton,
  Icon,
  Tooltip,
  Divider,
  Autocomplete,
} from "@mui/material";

import MDDialog from "components/MDDialog";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

import AddDynamicSpace from "./AddDynamicSpaces";
import { useMaterialUIController } from "context";

import { useDispatch, useSelector } from "react-redux";

import {
  getDynamicSpaces,
  deleteDynamicSpace,
} from "../../../store/slice/dynamicSpace/dynamicSpaceSlice";

import {
  assignSetting,
  getPlaceById,
  setSelectedPlace,
} from "store/slice/places/placeSlice";

import MDSnackbar from "components/MDSnackbar";
import DeleteDialog from "components/UIComponents/DeleteDialog";
import CircularIndeterminate from "components/MDLoading";
import moment from "moment";
import { getPlaces } from "store/slice/places/placeSlice";

const daysList = [
  "All",
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DynamicSpaceSettings = ({ open, onClose, place }) => {
  const dispatch = useDispatch();

  const dynamicSpaces = useSelector(
    (state) => state.dynamicSpaces?.dynamicSpaces || [],
  );
  const isLoading = useSelector(
    (state) => state.dynamicSpaces.loading || false,
  );

  const [enabled, setEnabled] = useState(place?.isDynamicSpaces || false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState("All");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [spaceId, setSpaceId] = useState(null);
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
  });

  const closeNotification = () =>
    setNotification((prev) => ({ ...prev, show: false }));

  useEffect(() => {
    setEnabled(!!place?.isDynamicSpaces);
  }, [place]);

  useEffect(() => {
    if (open && place?._id && selectedDay && place?.isDynamicSpaces) {
      dispatch(
        getDynamicSpaces({
          placeId: place._id,
          day: selectedDay,
        }),
      );
    }
  }, [dispatch, open, place?._id, place?.isDynamicSpaces, selectedDay]);

  const handleToggleDynamicSpace = (checked) => {
    setEnabled(checked);

    dispatch(
      assignSetting({
        placeId: place?._id,
        isDynamicSpaces: checked,
      }),
    )
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          dispatch(getPlaceById(place?._id))
            .unwrap()
            .then((result) => {
              if (result?.success) {
                dispatch(setSelectedPlace(result?.place));
                dispatch(getPlaces());
                dispatch(
                  getDynamicSpaces({
                    placeId: place._id,
                    day: selectedDay,
                  }),
                );
              }
            });
        }

        setNotification({
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
      })
      .catch((err) => {
        setNotification({
          color: "error",
          title: "Error",
          content: err?.message || "Something went wrong",
          icon: "warning",
          show: true,
        });
      });
  };

  const handleDelete = (id) => {
    dispatch(deleteDynamicSpace({ spaceId: id, status: 0 }))
      .unwrap()
      .then((res) => {
        setNotification({
          color: "success",
          icon: "check",
          title: "Deleted",
          content: res?.message,
          show: true,
        });
        setDeleteDialogOpen(false);
        dispatch(
          getDynamicSpaces({
            placeId: place?._id,
            day: selectedDay,
          }),
        );
      });
  };

  const handleStatus = (row) => {
    const newStatus = row.status === 10 ? 1 : 10;

    dispatch(deleteDynamicSpace({ spaceId: row?._id, status: newStatus })).then(
      () => {
        dispatch(getDynamicSpaces({ placeId: place?._id, day: selectedDay }));
      },
    );
  };

  const handleEdit = (row) => {
    setEditData(row);
    setDialogOpen(true);
  };

  const columns = [
    {
      field: "day",
      headerName: "Day",
      flex: 1,
      renderCell: (params) => {
        const day = params.value || "";
        return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
      },
    },
    {
      field: "startTime",
      headerName: "Start Time",
      flex: 0.6,
      renderCell: (params) =>
        params.value ? moment(params.value, "HH:mm").format("hh:mm A") : "",
    },
    {
      field: "endTime",
      headerName: "End Time",
      flex: 0.6,
      renderCell: (params) =>
        params.value ? moment(params.value, "HH:mm").format("hh:mm A") : "",
    },
    { field: "spaces", headerName: "Spaces", flex: 0.5 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      renderCell: (params) => (
        <Switch
          checked={params.row.status === 10}
          onChange={() => handleStatus(params.row)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              color="info"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <Icon>edit</Icon>
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Dynamic Space">
            <IconButton
              color="error"
              size="small"
              onClick={() => {
                setDeleteDialogOpen(true);
                setSpaceId(params.row?._id);
              }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <MDSnackbar
        {...notification}
        open={notification.show}
        close={closeNotification}
        bgWhite
      />

      <MDDialog
        dialogTitle="Dynamic Spaces"
        open={open}
        dialogClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: { xs: "95%", sm: "90%", md: "800px" },
            m: { xs: 1, sm: 2 },
          },
        }}
        closeIcon
      >
        <DialogContent sx={{ px: { xs: 1.5, sm: 2 } }}>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              Configure dynamic parking spaces for specific days and time
              ranges. This allows you to adjust available spaces based on
              demand.
            </MDTypography>
          </MDBox>

          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={2}
            borderRadius="lg"
            bgColor="grey-100"
            mb={3}
          >
            <MDBox>
              <MDTypography variant="h6">Enable Dynamic Spaces</MDTypography>
              <MDTypography variant="caption" color="text">
                Turn this on to configure dynamic parking capacity.
              </MDTypography>
            </MDBox>

            <Switch
              checked={enabled}
              onChange={(e) => handleToggleDynamicSpace(e.target.checked)}
            />
          </MDBox>

          {enabled && (
            <>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={2}
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={2}
                  width={{ xs: "100%", sm: "auto" }}
                >
                  <MDBox width={{ xs: "100%", sm: "170px" }}>
                    <Autocomplete
                      size="small"
                      options={daysList}
                      value={selectedDay}
                      onChange={(e, value) => setSelectedDay(value || "All")}
                      renderInput={(params) => (
                        <MDInput {...params} label="Select Day" fullWidth />
                      )}
                      disableClearable={selectedDay === "All"}
                    />
                  </MDBox>
                </MDBox>

                <MDButton
                  variant="contained"
                  color={sidenavColor}
                  size="small"
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  startIcon={<Icon>add</Icon>}
                  onClick={() => setDialogOpen(true)}
                >
                  Add Dynamic Space
                </MDButton>
              </MDBox>

              <Divider sx={{ mb: 2 }} />

              {dynamicSpaces?.length > 0 ? (
                <MDBox width="100%" overflow="auto">
                  <MDDataGrid
                    rows={dynamicSpaces}
                    columns={columns}
                    getRowId={(row) => row?._id}
                    autoHeight
                    disableSelectionOnClick
                    loading={isLoading}
                    sx={{ minWidth: 600 }}
                  />
                </MDBox>
              ) : (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={{ xs: 4, sm: 6 }}
                  px={2}
                  bgcolor="grey-50"
                  borderRadius="lg"
                >
                  <Icon color="disabled" sx={{ fontSize: 40 }}>
                    event_busy
                  </Icon>

                  <MDTypography variant="button" mt={1}>
                    No Dynamic Spaces Configured for {selectedDay}
                  </MDTypography>

                  <MDTypography variant="caption" mb={2}>
                    Click "Add Dynamic Space" to create one
                  </MDTypography>

                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => setDialogOpen(true)}
                  >
                    Add Dynamic Space
                  </MDButton>
                </MDBox>
              )}
            </>
          )}
        </DialogContent>
      </MDDialog>

      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..!"
        open={isLoading}
      />

      <AddDynamicSpace
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditData(null);
        }}
        place={place}
        editData={editData}
        selectedDay={selectedDay}
      />

      <DeleteDialog
        dialogOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        handelClick={() => handleDelete(spaceId)}
      />
    </>
  );
};

export default DynamicSpaceSettings;
